import { useEffect, useMemo, useRef, useState } from 'react';
import { IconCalendarTime, IconPiano, IconTargetArrow } from '@tabler/icons-react';
import { profile as profileService } from '@shared/api/services';
import { useProfile } from '@shared/api/useProfile';
import { useQueryClient } from '@tanstack/react-query';
import { useAvailableInstruments } from '@shared/api/useAvailableInstruments';
import { OnboardingLayout } from '@widgets/layout/ui/onboarding-layout';
import { Instrument } from './instrument';
import { Goals } from './goals';
import { StartTime } from './start-time';
import { RegistrationComplete } from './registration-complete';
import { StudentOnboardingFooter } from './footer';
import {
	areSameIdLists,
	areSameStudentPreferences,
	clearStudentOnboardingPrefillSearch,
	getPersistedStudentOnboardingPrefillSearch,
	getStudentOnboardingPrefill,
} from './lib/student-onboarding-prefill';
import {
	STUDENT_ONBOARDING_PREFERENCE_KEYS,
	resolveAvailableInstrumentByValue,
} from './lib/student-onboarding-options';

const { instrument: INSTRUMENT_KEY } = STUDENT_ONBOARDING_PREFERENCE_KEYS;

export const Onboarding = () => {
	const { data: profile } = useProfile();
	const { data: availableInstruments } = useAvailableInstruments();
	const queryClient = useQueryClient();
	const prefillAppliedRef = useRef(false);

	const onboardingPrefill = useMemo(
		() =>
			getStudentOnboardingPrefill({
				search: window.location.search,
				persistedSearch: getPersistedStudentOnboardingPrefillSearch(),
			}),
		[]
	);

	const [currentStep, setCurrentStep] = useState(onboardingPrefill.initialStep);
	const [selectedByQuestion, setSelectedByQuestion] = useState(
		onboardingPrefill.studentPreferences || {}
	);

	const steps = useMemo(
		() => [
			{
				id: 0,
				icon: IconPiano,
				title: 'Выбери инструмент',
				subtitle: 'Укажи инструмент, на котором хочешь заниматься',
				component: (
					<Instrument
						setCurrentStep={setCurrentStep}
						selectedByQuestion={selectedByQuestion}
						setSelectedByQuestion={setSelectedByQuestion}
						availableInstruments={availableInstruments}
					/>
				),
			},
			{
				id: 1,
				icon: IconTargetArrow,
				title: 'Уровень',
				subtitle: 'Выбери текущий уровень владения инструментом',
				component: (
					<Goals
						setCurrentStep={setCurrentStep}
						selectedByQuestion={selectedByQuestion}
						setSelectedByQuestion={setSelectedByQuestion}
						availableInstruments={availableInstruments}
					/>
				),
			},
			{
				id: 2,
				icon: IconCalendarTime,
				title: 'Время занятий',
				subtitle: 'Укажи, когда удобнее заниматься',
				component: (
					<StartTime
						setCurrentStep={setCurrentStep}
						selectedByQuestion={selectedByQuestion}
						setSelectedByQuestion={setSelectedByQuestion}
					/>
				),
			},
			{
				id: 3,
				hidden: true,
				component: <RegistrationComplete setCurrentStep={setCurrentStep} />,
			},
		],
		[availableInstruments, selectedByQuestion, setSelectedByQuestion, setCurrentStep]
	);

	useEffect(() => {
		if (!profile?.student_preferences || onboardingPrefill.hasStudentPreferences) return;
		if (Object.keys(profile.student_preferences).length <= 0) return;
		if (Object.keys(selectedByQuestion).length > 0) return;

		setSelectedByQuestion(profile.student_preferences);
	}, [profile, onboardingPrefill.hasStudentPreferences, selectedByQuestion]);

	useEffect(() => {
		if (!profile || !availableInstruments || !onboardingPrefill.hasRelevantParams || prefillAppliedRef.current) return;

		prefillAppliedRef.current = true;

		const currentInstrumentIds =
			profile?.instruments?.map((item) => item?.id)?.filter(Boolean) || [];
		const prefillInstrument = resolveAvailableInstrumentByValue(
			availableInstruments,
			onboardingPrefill.studentPreferences?.[INSTRUMENT_KEY]
		);
		const instrumentIds = prefillInstrument?.id
			? [prefillInstrument.id]
			: onboardingPrefill.instrumentIds;
		const updates = [];

		if (instrumentIds.length > 0 && !areSameIdLists(currentInstrumentIds, instrumentIds)) {
			updates.push(profileService.updateProfile({ instruments: instrumentIds }));
		}

		if (
			onboardingPrefill.hasCompleteStudentPreferences &&
			!areSameStudentPreferences(profile?.student_preferences, onboardingPrefill.studentPreferences)
		) {
			updates.push(
				profileService.updateUserPreference({
					student_preferences: onboardingPrefill.studentPreferences,
				})
			);
		}

		Promise.all(updates).finally(() => {
			queryClient.invalidateQueries(['profile']);
			clearStudentOnboardingPrefillSearch();
		});
	}, [profile, availableInstruments, onboardingPrefill, queryClient]);

	return (
		<OnboardingLayout
			steps={steps}
			currentStep={currentStep}
			onStepChange={setCurrentStep}
			showStepProgression
			footer={<StudentOnboardingFooter />}
		>
			{steps[currentStep]?.component}
		</OnboardingLayout>
	);
};